import React from 'react';
import { clsx } from 'clsx';
import { FunnelIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useUIStore } from '@/stores';

interface BaseFilterSidebarProps {
  title?: string;
  activeFilterCount?: number;
  onClearAll?: () => void;
  headerContent?: React.ReactNode;
  children: React.ReactNode;
}

export const BaseFilterSidebar: React.FC<BaseFilterSidebarProps> = ({
  title = 'Filters',
  activeFilterCount = 0,
  onClearAll,
  headerContent,
  children,
}) => {
  const filterSidebarCollapsed = useUIStore((state) => state.filterSidebarCollapsed);
  const toggleFilterSidebar = useUIStore((state) => state.toggleFilterSidebar);

  const hasActiveFilters = activeFilterCount > 0;

  return (
    <div className={clsx(
      'fixed inset-y-0 right-0 z-[9998] flex flex-col transition-all duration-300',
      filterSidebarCollapsed ? 'w-18' : 'w-72'
    )}>
      <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 shadow-lg border-l border-gray-200">
        {/* min-height (not fixed): the Trades saved-views selector adds a Save row
            when the view is dirty (LR-671); other sidebars keep the 64 px row. */}
        <div className="flex min-h-[4rem] shrink-0 items-center">
          <button
            type="button"
            className="-m-1 p-1 text-gray-400 hover:text-gray-500 relative"
            onClick={toggleFilterSidebar}
          >
            <span className="sr-only">Toggle filter sidebar</span>
            {filterSidebarCollapsed ? (
              <>
                <FunnelIcon className="h-[22px] w-[22px] stroke-[1.5]" aria-hidden="true" />
                {hasActiveFilters && (
                  <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-xs font-semibold text-white">
                    {activeFilterCount}
                  </span>
                )}
              </>
            ) : (
              <ChevronRightIcon className="h-4 w-4 stroke-2" aria-hidden="true" />
            )}
          </button>
          {!filterSidebarCollapsed && (
            headerContent ?? (
              <div className="flex items-center justify-between w-full ml-4">
                <h2 className="text-xl font-bold text-gray-900">{title}</h2>
                {hasActiveFilters && onClearAll && (
                  <button
                    onClick={onClearAll}
                    className="text-xs font-medium rounded transition-colors text-red-600 hover:text-red-800"
                  >
                    Clear All
                  </button>
                )}
              </div>
            )
          )}
        </div>

        {!filterSidebarCollapsed && (
          <div className="space-y-6 pb-4">
            {children}
          </div>
        )}
      </div>
    </div>
  );
};
